import { SORTABLE_DATE_FORMAT } from "modules/common/constants";
import { MakeMonthName } from "modules/common/models/period";
import { hex2rgba, shadeColor } from "modules/common/services/colors";
import moment from "moment";
import { PlanColumn, PlanPeriod } from "modules/spending/workload-plan/models/plan-types";
import {
    EMPTY_PROJECT_PLAN,
    PlanSegment,
    ProjectPaint,
    ProjectParticipant,
    ProjectPlanProject,
    ProjectPlanSnapshot,
    ProjectRowKind,
} from "./project-plan-types";

/** Прозрачность заливки полосы — та же, что в плановом табеле */
const BLOCK_ALPHA = 0.75;

/** На сколько затемнить цвет проекта для рамки */
const BORDER_SHADE = -30;

/** Ниже этой яркости подложки тёмная подпись сливается с заливкой */
const LIGHT_TEXT_THRESHOLD = 150;

const FALLBACK_COLOR = "#9b9b9b";

export interface ProjectPlanResponse {
    periods: ProjectPlanPeriodResponse[];
    orders: ProjectPlanOrderResponse[];
}

export interface ProjectPlanPeriodResponse {
    month: number;
    year: number;
    monthWorkDayCount: number;
    monthsWorkHours: number;
    days: ProjectPlanDayResponse[];
}

export interface ProjectPlanDayResponse {
    day: string;
    dayOfMonth: string;
    isDayOff: boolean;
}

export interface ProjectPlanOrderResponse {
    id: string;
    name: string;
    color: string;
    inventoryNumber: number;
    fullInventoryNumber: string;
    orderNumber: string;
    status: string;
    statusColor: string;
    employer: string;
    lastDay: string;
    /** Полный размах работ по проекту, посчитанный сервером по всем участникам */
    span: ProjectPlanSegmentResponse | null;
    rows: ProjectPlanRowResponse[];
}

export interface ProjectPlanSegmentResponse {
    /** Видимые границы, обрезанные бэком по запрошенному диапазону */
    from: string;
    to: string;
    /** Настоящий последний день отрезка */
    stopDay: string;
    /** Настоящая длина в рабочих днях */
    workDays: number;
    continuesLeft: boolean;
    continuesRight: boolean;
}

export interface ProjectPlanRowResponse {
    kind: ProjectRowKind;
    id: string;
    label: string;
    note: string;
    segments: ProjectPlanSegmentResponse[];
}

/**
 * Разворачивает ответ бэка в модель страницы: непрерывную ленту рабочих дней
 * и отрезки в координатах колонок. Выходные и праздники в колонки не попадают.
 */
export function buildProjectPlan(
    response: ProjectPlanResponse | null,
    visibleMonths: number,
    today: moment.Moment = moment().startOf("day")
): ProjectPlanSnapshot {
    if (!response) {
        return EMPTY_PROJECT_PLAN;
    }

    const columns: PlanColumn[] = [];
    const periods: PlanPeriod[] = [];

    (response.periods || []).forEach((period, periodIndex) => {
        const firstColumn = columns.length;

        (period.days || []).forEach((day) => {
            if (day.isDayOff) {
                return;
            }

            const date = moment(day.day, SORTABLE_DATE_FORMAT);

            columns.push({
                day: day.day,
                dayOfMonth: day.dayOfMonth,
                month: period.month,
                year: period.year,
                periodIndex,
                isPast: date.isBefore(today),
                isToday: date.isSame(today, "day"),
                hidden: periodIndex >= visibleMonths,
            });
        });

        periods.push({
            month: period.month,
            year: period.year,
            name: MakeMonthName(period.month),
            workDaysCount: period.monthWorkDayCount,
            workHours: period.monthsWorkHours,
            firstColumn,
            lastColumn: Math.max(firstColumn, columns.length - 1),
        });
    });

    // дни колонок отсортированы и сравнимы лексикографически (ISO) — этого хватает
    // и для точного попадания, и для отрезка, край которого пришёлся на выходной
    const days = columns.map((column) => column.day);

    const projects = (response.orders || [])
        .map((order) => toProject(order, days))
        .filter((project): project is ProjectPlanProject => project !== null);

    return { columns, periods, projects };
}

function toProject(order: ProjectPlanOrderResponse, days: string[]): ProjectPlanProject | null {
    const rows: ProjectParticipant[] = (order.rows || [])
        .map((row) => ({
            kind: row.kind,
            id: row.id,
            label: row.label,
            note: row.note,
            segments: (row.segments || [])
                .map((segment) => toSegment(segment, days))
                .filter((segment): segment is PlanSegment => segment !== null),
        }))
        .filter((row) => row.segments.length > 0);

    if (!rows.length) {
        return null;
    }

    return {
        id: order.id,
        name: order.name,
        color: order.color,
        inventoryNumber: order.inventoryNumber,
        fullInventoryNumber: order.fullInventoryNumber,
        orderNumber: order.orderNumber,
        status: order.status,
        statusColor: order.statusColor,
        employer: order.employer,
        lastDay: order.lastDay,
        paint: paintOf(order.color),
        rows,
        summary: order.span ? toSegment(order.span, days) : null,
    };
}

/** Отрезок дат → диапазон колонок. null, если в диапазон не попало ни одного рабочего дня */
function toSegment(segment: ProjectPlanSegmentResponse, days: string[]): PlanSegment | null {
    if (!segment.from || !segment.to || !days.length) {
        return null;
    }

    const start = lowerBound(days, segment.from);
    const end = upperBound(days, segment.to);

    if (start > end) {
        return null;
    }

    return {
        start,
        length: end - start + 1,
        workDays: segment.workDays,
        stopDay: segment.stopDay,
        continuesLeft: segment.continuesLeft,
        continuesRight: segment.continuesRight,
    };
}

/** Индекс первого дня, который не раньше value; days.length, если такого нет */
function lowerBound(days: string[], value: string): number {
    let low = 0;
    let high = days.length;

    while (low < high) {
        const middle = (low + high) >> 1;

        if (days[middle] < value) {
            low = middle + 1;
        } else {
            high = middle;
        }
    }

    return low;
}

/** Индекс последнего дня, который не позже value; -1, если такого нет */
function upperBound(days: string[], value: string): number {
    let low = 0;
    let high = days.length;

    while (low < high) {
        const middle = (low + high) >> 1;

        if (days[middle] <= value) {
            low = middle + 1;
        } else {
            high = middle;
        }
    }

    return low - 1;
}

function paintOf(color: string): ProjectPaint {
    const normalized = /^#[0-9a-f]{6}$/i.test(color) ? color : FALLBACK_COLOR;

    return {
        background: hex2rgba(normalized, BLOCK_ALPHA),
        borderColor: shadeColor(normalized, BORDER_SHADE),
        // полоса полупрозрачная и лежит на белом — контраст считаем по смешанному цвету
        dark: luminance(normalized, BLOCK_ALPHA) < LIGHT_TEXT_THRESHOLD,
        chipBackground: normalized,
        // плашка залита чистым цветом проекта — у неё своя яркость
        chipDark: luminance(normalized, 1) < LIGHT_TEXT_THRESHOLD,
    };
}

/**
 * Воспринимаемая яркость цвета, смешанного с белым фоном сетки при заданной
 * непрозрачности. По ней и выбирается цвет подписи: тёмный фон — светлый текст,
 * светлый фон — тёмный.
 */
function luminance(color: string, alpha: number): number {
    const value = parseInt(color.slice(1), 16);
    const mix = (channel: number) => channel * alpha + 255 * (1 - alpha);

    const r = mix((value >> 16) & 255);
    const g = mix((value >> 8) & 255);
    const b = mix(value & 255);

    return 0.299 * r + 0.587 * g + 0.114 * b;
}
